import { useState, useEffect } from 'react';

import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { useUpdateAdminApprovalLimit } from '@/api/mutations/useUpdateAdminApprovalLimit';
import { useUpdateTokenLimit } from '@/api/mutations/useUpdateTokenLimit';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import {
  SettingsContent,
  SettingsRow,
  SettingsSaveButton,
  SettingsSaveStatus,
  SettingsSection,
  SettingsSectionFooter,
} from '@/new/components/settings-section';
import { Input } from '@/new/components/shadcn/input';

function NoPermissionNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-3 px-4 py-3">
      <Icon className="lucide--info mt-0.5 h-5 w-5 text-gray-500" />
      <p className="text-sm leading-relaxed text-gray-600">{children}</p>
    </div>
  );
}

export function CreditLimits() {
  const { user } = useUserContext();
  const organisation = user?.organisation;
  const updateTokenLimitMutation = useUpdateTokenLimit();

  const [tokenLimit, setTokenLimit] = useState<string>('');
  const [higherApprovalLimit, setHigherApprovalLimit] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedTokenChanges, setHasUnsavedTokenChanges] = useState(false);
  const [hasUnsavedReviewerChanges, setHasUnsavedReviewerChanges] = useState(false);

  const { hasPermission } = useHasPermission();
  const updateAdminApprovalLimitMutation = useUpdateAdminApprovalLimit();
  const canManageLimits = hasPermission(Permission.TokenLimitsManage);

  const hasUnsavedChanges = hasUnsavedTokenChanges || hasUnsavedReviewerChanges;

  // Initialize values when organisation data is loaded
  useEffect(() => {
    if (organisation?.adminApprovalTokenLimit !== undefined && organisation.adminApprovalTokenLimit !== null) {
      setHigherApprovalLimit(organisation.adminApprovalTokenLimit.toString());
    }
    if (organisation?.tokenApprovalLimit !== undefined && organisation.tokenApprovalLimit !== null) {
      setTokenLimit(organisation.tokenApprovalLimit.toString());
    }
  }, [organisation]);

  const handleHigherApprovalLimitChange = (value: string) => {
    setHigherApprovalLimit(value);
    const originalValue = organisation?.adminApprovalTokenLimit?.toString() || '';
    setHasUnsavedReviewerChanges(value !== originalValue);
  };

  const handleTokenLimitChange = (value: string) => {
    setTokenLimit(value);
    const originalValue = organisation?.tokenApprovalLimit?.toString() || '';
    setHasUnsavedTokenChanges(value !== originalValue);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (hasUnsavedTokenChanges) {
        const tokenApprovalLimit = parseInt(tokenLimit, 10);
        if (isNaN(tokenApprovalLimit) || tokenApprovalLimit < 0) {
          toast.error('Please enter a valid credit limit');
          return;
        }

        await updateTokenLimitMutation.mutateAsync({ tokenApprovalLimit });
        setHasUnsavedTokenChanges(false);
      }

      if (hasUnsavedReviewerChanges) {
        const trimmed = higherApprovalLimit.trim();
        let adminApprovalTokenLimit: number | null;

        if (trimmed === '') {
          // Empty disables admin approval
          adminApprovalTokenLimit = null;
        } else {
          adminApprovalTokenLimit = parseInt(trimmed, 10);
          if (isNaN(adminApprovalTokenLimit) || adminApprovalTokenLimit < 0) {
            toast.error('Please enter a valid approval limit');
            return;
          }
        }

        await updateAdminApprovalLimitMutation.mutateAsync({ adminApprovalTokenLimit });
        setHasUnsavedReviewerChanges(false);
      }

      toast.success('Credit limits updated successfully');
    } catch {
      toast.error('Failed to save credit limits');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsContent>
      <SettingsSection
        title="Credit usage limits"
        description="Configure limits at which your users will need to approve uploads."
        footer={
          canManageLimits ? (
            <SettingsSectionFooter>
              <SettingsSaveStatus variant={hasUnsavedChanges ? 'unsaved' : 'saved'}>
                {hasUnsavedChanges ? 'You have unsaved changes' : 'All changes saved'}
              </SettingsSaveStatus>
              <SettingsSaveButton
                isLoading={isLoading}
                hasUnsavedChanges={hasUnsavedChanges}
                onClick={handleSave}
                disabled={!hasUnsavedChanges}
              />
            </SettingsSectionFooter>
          ) : undefined
        }
      >
        {canManageLimits ? (
          <>
            <SettingsRow
              title="Credit approval limit"
              description="Number of credits which can be uploaded per upload before users will have to explicitly click 'continue' before processing will happen."
            >
              <Input
                id="token-limit"
                type="number"
                className="w-28 text-right"
                value={tokenLimit}
                onChange={(e) => handleTokenLimitChange(e.target.value)}
                placeholder="e.g. 10000"
                min="0"
                step="1000"
              />
            </SettingsRow>

            <SettingsRow
              title={
                <>
                  Admin approval credit limit <span className="text-gray-400">(optional)</span>
                </>
              }
              description={
                <>
                  <p>
                    Uploads exceeding this limit will require approval from designated reviewers. Leave empty to disable
                    admin approval.
                  </p>
                  <p>
                    To make someone a reviewer, add the <b>runs:approve</b> permission to their role. Administrators
                    have this by default, or you can create a dedicated reviewer role in the{' '}
                    <NavLink to="/organisation-settings/roles" className="text-blue-600 underline hover:text-blue-700">
                      Roles
                    </NavLink>{' '}
                    section.
                  </p>
                </>
              }
            >
              <Input
                id="higher-approval-limit"
                type="number"
                className="w-28 text-right"
                value={higherApprovalLimit}
                onChange={(e) => handleHigherApprovalLimitChange(e.target.value)}
                placeholder="e.g. 50000"
                min="0"
                step="1000"
              />
            </SettingsRow>
          </>
        ) : (
          <NoPermissionNote>
            You do not have permission to manage credit limits. Please contact your administrator or the Wexler team.
          </NoPermissionNote>
        )}
      </SettingsSection>
    </SettingsContent>
  );
}
